/**
 * FlowHub - Reset Password Page
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Alert, message } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import AuthLayout from '../components/AuthLayout';
import { supabase } from '@/shared/lib/supabase';

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validToken, setValidToken] = useState<boolean | null>(null);
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [sendingResend, setSendingResend] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      // Verificar se há um hash de recuperação na URL
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      const type = hashParams.get('type');
      const errorParam = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');

      console.log('Reset Password - Params:', {
        hasAccessToken: !!accessToken,
        accessTokenValue: accessToken,
        accessTokenLength: accessToken?.length || 0,
        hasRefreshToken: !!refreshToken,
        type,
        error: errorParam,
        errorDescription
      });

      // Se há erro no hash (após redirect do /verify)
      if (errorParam) {
        if (errorParam === 'access_denied') {
          if (errorDescription?.includes('expired')) {
            setError('O link de recuperação expirou. Links são válidos por apenas 1 hora. Solicite um novo link abaixo.');
          } else if (errorDescription?.includes('invalid')) {
            setError('Link inválido ou já utilizado. Cada link só pode ser usado uma vez. Solicite um novo link abaixo.');
          } else {
            setError('Acesso negado. O link pode ter expirado ou já foi utilizado. Solicite um novo link abaixo.');
          }
        } else {
          setError(errorDescription?.replace(/\+/g, ' ') || 'Link inválido ou expirado. Solicite um novo link abaixo.');
        }
        setValidToken(false);
        return;
      }

      // Se tem access_token JWT válido (mais de 20 caracteres)
      if (accessToken && type === 'recovery' && accessToken.length > 20) {
        console.log('Token JWT encontrado no hash, estabelecendo sessão...');
        
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          });

          if (error) {
            console.error('Erro ao estabelecer sessão:', error);
            setError('Token inválido ou expirado. Solicite um novo link.');
            setValidToken(false);
            return;
          }

          console.log('Sessão estabelecida com sucesso:', data);
          setValidToken(true);
        } catch (err: any) {
          console.error('Erro ao processar token:', err);
          setError('Erro ao processar token. Solicite um novo link.');
          setValidToken(false);
        }
        return;
      }

      // Se não tem access_token, verificar se já há uma sessão ativa
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (session && !sessionError) {
          console.log('Sessão ativa detectada:', session);
          setValidToken(true);
          return;
        }
      } catch (err) {
        console.error('Erro ao verificar sessão:', err);
      }

      // Se chegou aqui, não tem token válido
      console.warn('Nenhum token válido encontrado');
      setError('Link inválido. Por favor, solicite um novo link de recuperação através da página de login.');
      setValidToken(false);
    };

    checkToken();
  }, []);

  const handleSubmit = async (values: ResetPasswordFormValues) => {
    try {
      setLoading(true);
      setError(null);

      if (values.password !== values.confirmPassword) {
        setError('As senhas não coincidem');
        return;
      }

      if (values.password.length < 6) {
        setError('A senha deve ter no mínimo 6 caracteres');
        return;
      }

      // Atualizar senha usando o token de recuperação
      const { error: updateError } = await supabase.auth.updateUser({
        password: values.password,
      });

      if (updateError) throw updateError;

      message.success('Senha atualizada com sucesso!');
      
      // Aguardar 2 segundos e redirecionar para login
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(err.message || 'Erro ao redefinir senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const requestNewLink = () => {
    setShowResendForm(true);
  };

  const handleResendEmail = async () => {
    try {
      setSendingResend(true);
      
      if (!resendEmail) {
        message.error('Digite seu email');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(resendEmail)) {
        message.error('Digite um email válido');
        return;
      }

      const { error: resendError } = await supabase.auth.resetPasswordForEmail(resendEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resendError) throw resendError;

      message.success('Novo email enviado! Verifique sua caixa de entrada e pasta de spam.', 6);
      setShowResendForm(false);
      setResendEmail('');
      
      // Opcional: redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Resend email error:', err);
      message.error(err.message || 'Erro ao enviar email. Tente novamente.');
    } finally {
      setSendingResend(false);
    }
  };

  // Se ainda está verificando o token
  if (validToken === null) {
    return (
      <AuthLayout
        title="Redefinir Senha"
        subtitle="Verificando seu link de recuperação..."
        features={[]}
        stats={[]}
      >
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto"></div>
        </div>
      </AuthLayout>
    );
  }

  // Se o token é inválido
  if (validToken === false) {
    return (
      <AuthLayout
        title="Link Expirado"
        subtitle="O link de recuperação não é mais válido."
        features={[
          'Links expiram após 1 hora por segurança',
          'Você pode solicitar um novo link imediatamente',
          'Cada link só pode ser usado uma vez'
        ]}
        stats={[
          { label: 'Validade do link', value: '1 hora' },
          { label: 'Tentativas', value: 'Ilimitadas' }
        ]}
      >
        <Alert
          message="Link Expirado ou Inválido"
          description={
            <div className="space-y-2">
              <p>{error}</p>
              <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs text-slate-600">
                <strong>Por que isso acontece?</strong>
                <ul className="mt-2 ml-4 space-y-1 list-disc">
                  <li>O link expirou (mais de 1 hora após envio)</li>
                  <li>O link já foi usado anteriormente</li>
                  <li>Você solicitou um novo link (o anterior é cancelado)</li>
                </ul>
                <p className="mt-2">
                  <strong>Solução:</strong> Clique no botão abaixo para solicitar um novo link.
                  O novo link será enviado para seu email cadastrado.
                </p>
              </div>
            </div>
          }
          type="warning"
          showIcon
          className="mb-6 rounded-xl"
        />

        {!showResendForm ? (
          <>
            <Button
              type="primary"
              onClick={requestNewLink}
              block
              size="large"
              className="!h-12 !rounded-xl !bg-gradient-to-r !from-violet-600 !to-pink-600 hover:!from-violet-700 hover:!to-pink-700 !border-0 !shadow-lg !shadow-violet-500/50 !font-semibold !text-base"
            >
              Solicitar Novo Link
            </Button>

            <Button
              type="link"
              onClick={() => navigate('/login')}
              block
              className="mt-4"
            >
              Voltar ao Login
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <Alert
              message="Digite seu email cadastrado"
              description="Enviaremos um novo link de recuperação válido por 1 hora."
              type="info"
              showIcon
              className="rounded-xl"
            />
            
            <Input
              prefix={<MailOutlined className="text-violet-500" />}
              placeholder="seu@email.com"
              value={resendEmail}
              onChange={(e) => setResendEmail(e.target.value)}
              size="large"
              type="email"
              onPressEnter={handleResendEmail}
              autoFocus
            />

            <div className="flex gap-3">
              <Button
                type="primary"
                onClick={handleResendEmail}
                loading={sendingResend}
                size="large"
                className="flex-1 !h-12 !rounded-xl !bg-gradient-to-r !from-violet-600 !to-pink-600 hover:!from-violet-700 hover:!to-pink-700 !border-0 !shadow-lg !shadow-violet-500/50 !font-semibold"
              >
                Enviar Novo Link
              </Button>
              
              <Button
                onClick={() => setShowResendForm(false)}
                size="large"
                className="!h-12 !rounded-xl"
              >
                Cancelar
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 rounded-xl text-sm text-blue-900">
          <strong>💡 Dica:</strong> Após solicitar o novo link, use-o em até 1 hora e evite
          clicar múltiplas vezes no mesmo link de email.
        </div>
      </AuthLayout>
    );
  }

  // Token válido - mostrar formulário
  return (
    <AuthLayout
      title="Redefinir Senha"
      subtitle="Digite sua nova senha de acesso."
      features={[
        'Senha segura e criptografada',
        'Proteção de dados garantida',
        'Acesso imediato após redefinição'
      ]}
      stats={[
        { label: 'Criptografia', value: 'AES-256' },
        { label: 'Segurança', value: 'Máxima' }
      ]}
    >
      {error && (
        <Alert
          message={error}
          type="error"
          closable
          onClose={() => setError(null)}
          className="mb-6 rounded-xl shadow-lg"
        />
      )}

      <Form<ResetPasswordFormValues>
        name="reset-password"
        onFinish={handleSubmit}
        layout="vertical"
        size="large"
        autoComplete="off"
        className="space-y-4"
      >
        <Form.Item
          name="password"
          label={<span className="text-slate-700 font-medium">Nova Senha</span>}
          rules={[
            { required: true, message: 'Informe sua nova senha' },
            { min: 6, message: 'A senha deve ter no mínimo 6 caracteres' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-violet-500" />}
            placeholder="••••••••"
            autoComplete="new-password"
            className="rounded-xl h-12 border-slate-200 hover:border-violet-400 focus:border-violet-500"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label={<span className="text-slate-700 font-medium">Confirmar Nova Senha</span>}
          rules={[
            { required: true, message: 'Confirme sua nova senha' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('As senhas não coincidem'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="text-violet-500" />}
            placeholder="••••••••"
            autoComplete="new-password"
            className="rounded-xl h-12 border-slate-200 hover:border-violet-400 focus:border-violet-500"
          />
        </Form.Item>

        <div className="text-sm text-slate-500">
          A senha deve ter no mínimo 6 caracteres. Use letras, números e símbolos para maior segurança.
        </div>

        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          block
          size="large"
          className="!h-12 !rounded-xl !bg-gradient-to-r !from-violet-600 !to-pink-600 hover:!from-violet-700 hover:!to-pink-700 !border-0 !shadow-lg !shadow-violet-500/50 !font-semibold !text-base"
        >
          Redefinir Senha
        </Button>
      </Form>

      <div className="mt-8 text-center text-sm text-slate-600">
        Lembrou sua senha?{' '}
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="font-semibold text-violet-600 hover:text-pink-600 transition-colors"
        >
          Voltar ao login
        </button>
      </div>
    </AuthLayout>
  );
}

export default ResetPasswordPage;
